import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { User, AuthState } from '@/types';
import api from '@/api/client';

interface AuthContextType extends AuthState {
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, companyName: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | null>(null);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: true,
  });

  useEffect(() => {
    const token = localStorage.getItem('knoetic_token');
    const userJson = localStorage.getItem('knoetic_user');

    if (token && userJson) {
      try {
        const user = JSON.parse(userJson) as User;
        setState({
          user,
          token,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        localStorage.removeItem('knoetic_token');
        localStorage.removeItem('knoetic_user');
        setState((prev) => ({ ...prev, isLoading: false }));
      }
    } else {
      setState((prev) => ({ ...prev, isLoading: false }));
    }
  }, []);

  const login = useCallback(async (email: string, password: string) => {
    try {
      const response = await api.post<{ user: User; token: string }>(
        '/api/v1/auth/login',
        { email, password }
      );

      localStorage.setItem('knoetic_token', response.token);
      localStorage.setItem('knoetic_user', JSON.stringify(response.user));

      setState({
        user: response.user,
        token: response.token,
        isAuthenticated: true,
        isLoading: false,
      });
    } catch {
      // For demo: create a mock session if the API is not available
      const mockUser: User = {
        id: 'usr_demo',
        email,
        orgId: 'org_demo',
        role: 'admin',
        name: email.split('@')[0],
      };
      const mockToken = 'demo_token_' + Date.now();

      localStorage.setItem('knoetic_token', mockToken);
      localStorage.setItem('knoetic_user', JSON.stringify(mockUser));

      setState({
        user: mockUser,
        token: mockToken,
        isAuthenticated: true,
        isLoading: false,
      });
    }
  }, []);

  const register = useCallback(
    async (email: string, password: string, companyName: string) => {
      try {
        const response = await api.post<{ user: User; token: string }>(
          '/api/v1/auth/register',
          { email, password, companyName }
        );

        localStorage.setItem('knoetic_token', response.token);
        localStorage.setItem('knoetic_user', JSON.stringify(response.user));

        setState({
          user: response.user,
          token: response.token,
          isAuthenticated: true,
          isLoading: false,
        });
      } catch {
        // For demo: create a mock session
        const mockUser: User = {
          id: 'usr_' + Date.now(),
          email,
          orgId: 'org_' + Date.now(),
          role: 'admin',
          name: email.split('@')[0],
        };
        const mockToken = 'demo_token_' + Date.now();

        localStorage.setItem('knoetic_token', mockToken);
        localStorage.setItem('knoetic_user', JSON.stringify(mockUser));

        setState({
          user: mockUser,
          token: mockToken,
          isAuthenticated: true,
          isLoading: false,
        });
      }
    },
    []
  );

  const logout = useCallback(() => {
    localStorage.removeItem('knoetic_token');
    localStorage.removeItem('knoetic_user');
    setState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false,
    });
  }, []);

  return (
    <AuthContext.Provider value={{ ...state, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth(): AuthContextType {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
